import React, { useState, useEffect, useRef } from 'react';
import { FiEdit2, FiX, FiCheck, FiClock, FiCalendar, FiClock as FiClockOut } from 'react-icons/fi';
import axios from 'axios';
import { jwtDecode } from "jwt-decode";

// Calculate work hours between punch in and punch out
const calculateWorkHours = (punchIn, punchOut) => {
  if (!punchIn || !punchOut) return '0:00';
  
  const [inHour, inMinute] = punchIn.split(':').map(Number);
  const [outHour, outMinute] = punchOut.split(':').map(Number);
  
  const inTotalMinutes = inHour * 60 + inMinute;
  const outTotalMinutes = outHour * 60 + outMinute;
  
  let diffMinutes = outTotalMinutes - inTotalMinutes;
  if (diffMinutes < 0) diffMinutes += 24 * 60; // Handle next day
  
  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;
  
  return `${hours}:${minutes.toString().padStart(2, '0')}`;
};

// Format time to 12-hour format with AM/PM
const formatTo12Hour = (time24) => {
  if (!time24 || time24 === '-' || time24 === '--:--') return time24;
  
  try {
    const [hours, minutes] = time24.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return time24;
    
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  } catch (error) {
    return time24; // Return original if formatting fails
  }
};

// Convert 12-hour format back to 24-hour format for calculations
const convertTo24Hour = (time12) => {
  if (!time12 || time12 === '-' || time12 === '--:--') return time12;
  
  try {
    const [time, period] = time12.split(' ');
    if (!time || !period) return time12; // Not in 12-hour format, return as is
    
    const [hours, minutes] = time.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return time12;
    
    let hour24 = hours;
    if (period.toUpperCase() === 'PM' && hours !== 12) {
      hour24 = hours + 12;
    } else if (period.toUpperCase() === 'AM' && hours === 12) {
      hour24 = 0;
    }
    
    return `${hour24.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  } catch (error) {
    return time12; // Return original if conversion fails
  }
};

const calculateLateTime = (punchInTime) => {
  try {
    // Convert 12-hour format to 24-hour format for calculation
    const time24 = convertTo24Hour(punchInTime);
    const [hours, minutes] = time24.split(':').map(Number);
    
    if (isNaN(hours) || isNaN(minutes)) {
      return { isLate: false, lateTime: '0m' };
    }
    
    const punchInDate = new Date();
    punchInDate.setHours(hours, minutes, 0, 0);
    
    const lateThreshold = new Date();
    lateThreshold.setHours(10, 15, 0, 0); // 10:15 AM
    
    if (punchInDate > lateThreshold) {
      const diffMs = punchInDate - lateThreshold;
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      const lateHours = Math.floor(diffMinutes / 60);
      const lateMinutes = diffMinutes % 60;
      
      return {
        isLate: true,
        lateTime: lateHours > 0 ? `${lateHours}h ${lateMinutes}m` : `${lateMinutes}m`
      };
    }
    
    return { isLate: false, lateTime: '0m' };
  } catch (error) {
    return { isLate: false, lateTime: '0m' };
  }
};

const DailyAttendance = () => {
  const [currentAttendance, setCurrentAttendance] = useState(null);
  const [punchInTime, setPunchInTime] = useState(null);
  const [punchOutTime, setPunchOutTime] = useState(null);
  const [workHours, setWorkHours] = useState('00:00');
  const [attendanceStatus, setAttendanceStatus] = useState('not-started');
  const [workReport, setWorkReport] = useState('');
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: 'info' // 'info', 'success', 'error', 'warning'
  });
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showCamera, setShowCamera] = useState(false);
  const [capturedPunchInImage, setCapturedPunchInImage] = useState(null);
  const [capturedPunchOutImage, setCapturedPunchOutImage] = useState(null);
  const [cameraAction, setCameraAction] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'
  const [selectedDate, setSelectedDate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingReport, setEditingReport] = useState(false);
  const [tempReport, setTempReport] = useState('');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [workReportSubmitted, setWorkReportSubmitted] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [employeeShift, setEmployeeShift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [loadingHolidays, setLoadingHolidays] = useState(false);
  const [holidaysError, setHolidaysError] = useState(null);


  const videoRef = useRef(null);
  const streamRef = useRef(null);


  const formatTimeUTC = (date) => {
  if (!date) return '-';
  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  const time24 = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  return formatTo12Hour(time24);
};


  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (attendanceStatus === 'punched-in' && punchInTime) {
      const timer = setInterval(() => {
        const now = new Date();
        const diffMs = now - punchInTime;
        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        setWorkHours(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [attendanceStatus, punchInTime]);

  // Enhanced attendance fetching with consistent data processing
  const fetchAllAttendanceRecords = async () => {
    if (!employee || !employee._id) {
      return [];
    }

    const token = localStorage.getItem("authToken");
    if (!token) {
      return [];
    }

    try {
      const response = await axios.get(
        `https://customersupport-jhy9.onrender.com/api/attendance`,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          timeout: 15000,
          validateStatus: (status) => status < 500
        }
      );

      // Handle authentication errors
      if (response.status === 401) {
        localStorage.removeItem('authToken');
        setNotification({
          show: true,
          message: 'Session expired. Please log in again.',
          type: 'error',
          autoHide: true
        });
        return [];
      }
      
      if (response.status === 403) {
        setNotification({
          show: true,
          message: 'Access denied. Please check your permissions.',
          type: 'error',
          autoHide: true
        });
        return [];
      }

      // Handle different response formats
      let attendanceRecords = [];
      if (Array.isArray(response.data)) {
        attendanceRecords = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        attendanceRecords = response.data.data;
      } else if (response.data?.records && Array.isArray(response.data.records)) {
        attendanceRecords = response.data.records;
      } else if (response.data?.data) {
        attendanceRecords = [response.data.data];
      } else if (response.data) {
        attendanceRecords = [response.data];
      }

      // Get company ID for filtering
      let companyId = employee.company;
      if (!companyId) {
        try {
          const decoded = jwtDecode(token);
          companyId = decoded.companyId || decoded.company?._id || decoded.orgId;
        } catch (err) {
          // Continue without company filter if token decode fails
        }
      }

      // Filter and process records for current employee and company
      const processedRecords = attendanceRecords
        .filter(record => {
          if (!record) return false;
          
          // Match employee ID (handle both string and object references)
          const employeeMatch = record.employee === employee._id || 
                               record.employee?._id === employee._id ||
                               record.employee?.id === employee._id;
          
          // Match company ID (handle both string and object references)
          const companyMatch = !companyId || 
                              record.company === companyId || 
                              record.company?._id === companyId ||
                              record.company?.id === companyId;
          
          return employeeMatch && companyMatch;
        })
        .map(record => {
          const inTime = record.inTime ? new Date(record.inTime) : null;
          const outTime = record.outTime ? new Date(record.outTime) : null;
          
          // Determine status based on data (consistent with fetchAttendanceByDate)
          let status = record.status || 'Present';
          if (!inTime) {
            status = 'Absent';
          } else if (inTime && !outTime) {
            status = 'Incomplete';
          } else if (inTime && outTime) {
            // Calculate work hours to determine if it's a half day
            const workMinutes = (outTime - inTime) / (1000 * 60);
            if (workMinutes < 240) { // Less than 4 hours
              status = 'Half-day';
            } else {
              status = 'Present';
            }
          }
          
          // Parse date properly
          let recordDate = null;
          if (record.date) {
            try {
              recordDate = new Date(record.date).toISOString().split('T')[0];
            } catch (e) {
              recordDate = inTime ? inTime.toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
            }
          } else if (inTime) {
            recordDate = inTime.toISOString().split('T')[0];
          } else {
            recordDate = new Date().toISOString().split('T')[0];
          }
          console.log("fetchedRecord---",record);
          
          return {
            _id: record._id,
            date: recordDate,
            status: status,
            // punchIn: inTime ? formatTo12Hour(inTime.toTimeString().substring(0, 5)) : '-',
            // punchOut: outTime ? formatTo12Hour(outTime.toTimeString().substring(0, 5)) : '-',
            punchIn: inTime ? formatTimeUTC(inTime) : '-',
punchOut: outTime ? formatTimeUTC(outTime) : '-',

            hours: inTime && outTime ? calculateWorkHours(
              inTime.toTimeString().substring(0, 5),
              outTime.toTimeString().substring(0, 5)
            ) : (inTime ? 'Ongoing' : '--:--'),
            punchInImage: record.inPhoto || null,
            punchOutImage: record.outPhoto || null,
            workingTime: record.workingTime || 0,
            inLocation: record.inLocation || 'Office',
            outLocation: record.outLocation || 'Office',
            report: record.workReport || record.report || '',
            // Additional metadata
            rawData: record // Keep original data for debugging
          };
        })
        .sort((a, b) => new Date(b.date) - new Date(a.date)); // Sort by date descending

      return processedRecords;

    } catch (error) {
      // Enhanced error handling
      if (error.code === 'ECONNABORTED') {
        setNotification({
          show: true,
          message: 'Request timeout. Please check your internet connection.',
          type: 'error',
          autoHide: true
        });
      } else if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        
        let errorMessage = 'Failed to load attendance data.';
        switch (status) {
          case 401:
            localStorage.removeItem('authToken');
            errorMessage = 'Session expired. Please log in again.';
            break;
          case 403:
            errorMessage = 'Access denied. Please check your permissions.';
            break;
          case 404:
            errorMessage = 'Attendance service not found. Please contact support.';
            break;
          case 429:
            errorMessage = 'Too many requests. Please wait a moment and try again.';
            break;
          case 500:
            errorMessage = 'Server error. Please try again later.';
            break;
          default:
            errorMessage = data?.message || `Server error (${status}). Please try again.`;
        }
        
        setNotification({
          show: true,
          message: errorMessage,
          type: 'error',
          autoHide: true
        });
      } else if (error.request) {
        setNotification({
          show: true,
          message: 'Network error. Please check your internet connection.',
          type: 'error',
          autoHide: true
        });
      }
      
      return [];
    }
  };

  // Fetch attendance history using the enhanced function
  const fetchAttendanceHistory = async () => {
    const records = await fetchAllAttendanceRecords();
    setAttendanceHistory(records);
  };

  useEffect(() => {
    fetchAttendanceHistory();
  }, [employee?._id]); // Fetch when employee data is available

  useEffect(() => {
    const filtered = attendanceHistory.filter(record => {
      const recordDate = new Date(record.date);
      return recordDate.getFullYear() === selectedYear && 
             recordDate.getMonth() === selectedMonth;
    });
    setFilteredHistory(filtered);
  }, [attendanceHistory, selectedYear, selectedMonth]);

  useEffect(() => {
    if (showCamera) {
      startCamera();
    } else {
      stopCamera();
    }
    
    return () => {
      stopCamera();
    };
  }, [showCamera]);



useEffect(() => {
  const fetchCompany = async () => {
    // Wait for employee data to be loaded first
    if (!employee || !employee.company) {
      return;
    }

    const token = localStorage.getItem("authToken") || localStorage.getItem("token");

    if (!token) {
      const errorMsg = "No access token found. Please log in.";
      setError(errorMsg);
      setLoading(false);
      return;
    }

    const companyId = employee.company;

    try {

      const response = await axios.get(
        `https://customersupport-jhy9.onrender.com/api/companies/${companyId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );


      if (response.data) {
        const companyData = response.data.data || response.data;
        setSelectedCompany(companyData);
      } else {
        throw new Error("Empty company response received.");
      }
    } catch (err) {
      setError("Failed to fetch company data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  fetchCompany();
}, [employee?._id]); // Depend on employee data


useEffect(() => {
  const fetchEmployee = async () => {
    const token = localStorage.getItem("authToken") || localStorage.getItem("token");

    if (!token) {
      const errorMsg = "No access token found. Please log in.";
      setError(errorMsg);
      setLoading(false);
      return;
    }

    // ✅ Decode employeeId from token
    let employeeId;
    try {
      const decoded = jwtDecode(token);
      
      employeeId = decoded.employeeId || decoded.id || decoded._id || decoded.userId;
    } catch (err) {
      localStorage.removeItem("authToken");
      localStorage.removeItem("token");
      setError("Invalid or expired token. Please log in again.");
      setLoading(false);
      return;
    }

    if (!employeeId) {
      const errorMsg = "Employee ID not found in token. Please log in again.";
      setError(errorMsg);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(
        `https://customersupport-jhy9.onrender.com/api/employees/${employeeId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );


      if (response.data) {
        // Normalize data (supporting both { data: {...} } and direct object response)
        const employeeData = response.data.data || response.data;
        setEmployee(employeeData);
      } else {
        throw new Error("Empty response data received");
      }
    } catch (err) {
      const errorDetails = {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        config: {
          url: err.config?.url,
          method: err.config?.method,
          headers: err.config?.headers
            ? {
                ...err.config.headers,
                Authorization: "[REDACTED]", // hide token in logs
              }
            : null,
        },
      };
      setError(`Error: ${err.message}. Check console for details.`);
    } finally {
      setLoading(false);
    }
  };

  fetchEmployee();
}, []);


  useEffect(() => {
    if (employee) {
      fetchEmployeeShift();
    }
  }, [employee?._id]);

  // Fetch holidays when company data is available
  useEffect(() => {
    if (selectedCompany && selectedCompany._id) {
      fetchHolidays();
    }
  }, [selectedCompany?._id]);

  // Fetch employee shift information
  const fetchEmployeeShift = async () => {
    if (!employee || !employee._id) {
      return;
    }

    try {
      
      // Extract shift information from employee data
      const shiftInfo = {
        shiftName: employee.shift?.name || employee.shiftName || 'Standard Shift',
        startTime: employee.shift?.startTime || employee.startTime || '09:00',
        endTime: employee.shift?.endTime || employee.endTime || '17:00',
        workingHours: employee.shift?.workingHours || employee.workingHours || 8,
        breakTime: employee.shift?.breakTime || employee.breakTime || 60, // in minutes
        shiftType: employee.shift?.type || employee.shiftType || 'Regular'
      };

      setEmployeeShift(shiftInfo);
      
    } catch (error) {
      // Set default shift if fetch fails
      setEmployeeShift({
        shiftName: 'Standard Shift',
        startTime: '09:00',
        endTime: '17:00',
        workingHours: 8,
        breakTime: 60,
        shiftType: 'Regular'
      });
    }
  };

  // 🎯 COMPREHENSIVE HOLIDAY FETCHING FUNCTIONALITY
  const fetchHolidays = async () => {
    // Only fetch if company data is available
    if (!selectedCompany || !selectedCompany._id) {
      console.log('🏢 Company data not available for holiday fetching');
      return;
    }

    const token = localStorage.getItem("authToken");
    if (!token) {
      console.log('🔑 No access token found for holiday fetching');
      setHolidaysError('No access token found. Please log in.');
      return;
    }

    setLoadingHolidays(true);
    setHolidaysError(null);

    try {
      console.log('🚀 FETCHING HOLIDAYS FOR COMPANY:', selectedCompany._id);
      
      // JWT token validation
      let decoded;
      try {
        decoded = jwtDecode(token);
        console.log('🔍 HOLIDAY FETCH TOKEN DECODED:', {
          id: decoded.id,
          role: decoded.role,
          companyId: decoded.companyId || 'Not in token',
          exp: new Date(decoded.exp * 1000).toLocaleString()
        });
      } catch (tokenError) {
        console.error('❌ Invalid token for holiday fetch:', tokenError);
        localStorage.removeItem('authToken');
        setHolidaysError('Invalid token. Please log in again.');
        return;
      }

      const response = await axios.get(
        `https://customersupport-jhy9.onrender.com/api/holidays/company/${selectedCompany._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          timeout: 15000,
          validateStatus: (status) => status < 500
        }
      );

      console.log('📡 HOLIDAYS API RESPONSE:', {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        dataType: typeof response.data,
        dataLength: Array.isArray(response.data) ? response.data.length : 'Not array',
        data: response.data
      });

      // Handle authentication errors
      if (response.status === 401) {
        localStorage.removeItem('authToken');
        setHolidaysError('Session expired. Please log in again.');
        setNotification({
          show: true,
          message: 'Session expired. Please log in again.',
          type: 'error',
          autoHide: true
        });
        return;
      }
      
      if (response.status === 403) {
        setHolidaysError('Access denied. Please check your permissions.');
        setNotification({
          show: true,
          message: 'Access denied to holiday data.',
          type: 'error',
          autoHide: true
        });
        return;
      }

      if (response.status === 404) {
        console.log('📅 No holidays found for company');
        setHolidays([]);
        setNotification({
          show: true,
          message: 'No holidays configured for your company.',
          type: 'info',
          autoHide: true
        });
        return;
      }

      // Handle different response formats
      let holidayRecords = [];
      if (Array.isArray(response.data)) {
        holidayRecords = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        holidayRecords = response.data.data;
      } else if (response.data?.holidays && Array.isArray(response.data.holidays)) {
        holidayRecords = response.data.holidays;
      } else if (response.data?.data) {
        holidayRecords = [response.data.data];
      } else if (response.data) {
        holidayRecords = [response.data];
      }

      console.log('🎯 PROCESSED HOLIDAYS:', {
        totalHolidays: holidayRecords.length,
        holidays: holidayRecords.map((holiday, index) => ({
          index: index + 1,
          id: holiday._id || holiday.id,
          name: holiday.name || holiday.holidayName || holiday.title,
          date: holiday.date || holiday.holidayDate,
          description: holiday.description || holiday.details,
          type: holiday.type || holiday.holidayType || 'General',
          isActive: holiday.isActive !== undefined ? holiday.isActive : true,
          company: holiday.company || 'Not specified',
          createdAt: holiday.createdAt,
          updatedAt: holiday.updatedAt
        }))
      });

      // Process and validate holiday data
      const processedHolidays = holidayRecords
        .filter(holiday => {
          // Basic validation
          if (!holiday) return false;
          
          // Must have a date
          const holidayDate = holiday.date || holiday.holidayDate;
          if (!holidayDate) {
            console.log('⚠️ Holiday missing date:', holiday);
            return false;
          }
          
          // Validate date format
          try {
            new Date(holidayDate);
            return true;
          } catch (e) {
            console.log('⚠️ Invalid holiday date format:', holidayDate);
            return false;
          }
        })
        .map(holiday => {
          const holidayDate = holiday.date || holiday.holidayDate;
          return {
            _id: holiday._id || holiday.id,
            name: holiday.name || holiday.holidayName || holiday.title || 'Holiday',
            date: new Date(holidayDate).toISOString().split('T')[0], // Format as YYYY-MM-DD
            description: holiday.description || holiday.details || '',
            type: holiday.type || holiday.holidayType || 'General',
            isActive: holiday.isActive !== undefined ? holiday.isActive : true,
            company: holiday.company,
            originalData: holiday // Keep for debugging
          };
        })
        .filter(holiday => holiday.isActive) // Only include active holidays
        .sort((a, b) => new Date(a.date) - new Date(b.date)); // Sort by date

      console.log('✅ FINAL PROCESSED HOLIDAYS:', {
        count: processedHolidays.length,
        holidays: processedHolidays
      });

      setHolidays(processedHolidays);
      
      // Show success notification
      if (processedHolidays.length > 0) {
        setNotification({
          show: true,
          message: `✅ Loaded ${processedHolidays.length} company holidays`,
          type: 'success',
          autoHide: true
        });
      }

    } catch (error) {
      console.error('❌ HOLIDAY FETCH ERROR:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          timeout: error.config?.timeout
        }
      });

      // Enhanced error handling
      if (error.code === 'ECONNABORTED') {
        setHolidaysError('Request timeout. Please check your internet connection.');
        setNotification({
          show: true,
          message: 'Holiday fetch timeout. Please try again.',
          type: 'error',
          autoHide: true
        });
      } else if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        
        let errorMessage = 'Failed to load company holidays.';
        switch (status) {
          case 401:
            localStorage.removeItem('authToken');
            errorMessage = 'Session expired. Please log in again.';
            break;
          case 403:
            errorMessage = 'Access denied to holiday data.';
            break;
          case 404:
            errorMessage = 'No holidays found for your company.';
            setHolidays([]); // Set empty array for 404
            break;
          case 429:
            errorMessage = 'Too many requests. Please wait a moment and try again.';
            break;
          case 500:
            errorMessage = 'Server error. Please try again later.';
            break;
          default:
            errorMessage = data?.message || `Server error (${status}). Please try again.`;
        }
        
        setHolidaysError(errorMessage);
        setNotification({
          show: true,
          message: errorMessage,
          type: 'error',
          autoHide: true
        });
      } else if (error.request) {
        setHolidaysError('Network error. Please check your internet connection.');
        setNotification({
          show: true,
          message: 'Network error loading holidays.',
          type: 'error',
          autoHide: true
        });
      } else {
        setHolidaysError('Unexpected error loading holidays.');
        setNotification({
          show: true,
          message: 'Unexpected error loading holidays.',
          type: 'error',
          autoHide: true
        });
      }
    } finally {
      setLoadingHolidays(false);
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user' } 
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
      }
    } catch (err) {
      alert("Could not access camera. Please check permissions.");
      setShowCamera(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  // Function to compress image - single implementation
  const compressImage = (imageDataUrl, maxWidth = 800, quality = 0.7) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = imageDataUrl;
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions
        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxWidth) {
            width *= maxWidth / height;
            height = maxWidth;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to JPEG with specified quality
        const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
        resolve(compressedDataUrl);
      };
      
      img.onerror = () => {
        resolve(imageDataUrl); // Return original if compression fails
      };
    });
  };

  const handlePunchIn = async () => {
    setCameraAction('punchin');
    setShowCamera(true);
  };

  const handlePunchOut = async () => {
    if (!workReportSubmitted) {
      alert('Please submit your work report before punching out.');
      return;
    }
    setCameraAction('punchout');
    setShowCamera(true);
  };

  const handleSubmitReport = () => {
    if (workReport.trim() === '') {
      alert('Please provide a work report before submitting.');
      return;
    }
    
    setWorkReportSubmitted(true);
    alert('Work report submitted successfully! You may now punch out.');
  };

  const captureImage = async () => {
    if (!videoRef.current) {
      const error = 'Video reference not available';
      setNotification({
        show: true,
        message: 'Camera not available. Please ensure your camera is connected and permissions are granted.',
        type: 'error'
      });
      throw new Error(error);
    }
    
    try {
      
      // Capture the image
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      
      if (canvas.width === 0 || canvas.height === 0) {
        throw new Error('Invalid video dimensions. Please ensure the camera is properly connected.');
      }
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
      
      // Convert to data URL and compress
      const imageDataUrl = canvas.toDataURL('image/jpeg', 0.8);
      
      const compressedImage = await compressImage(imageDataUrl);
      
      const now = new Date();
      const today = now.toISOString().split('T')[0];
      
      // Get IDs from employee data and JWT token
      let employeeId, companyId;
      
      if (employee) {
        employeeId = employee._id;
        companyId = employee.company;
      } else {
        // Fallback to JWT token
        try {
          const token = localStorage.getItem("authToken");
          const decoded = jwtDecode(token);
          employeeId = decoded.employeeId || decoded.id || decoded._id || decoded.userId;
          companyId = decoded.companyId || decoded.company;
        } catch (err) {
        }
      }
      
      if (!employeeId || !companyId) {
        throw new Error('Missing employee or company information. Please ensure you are properly logged in and employee data is loaded.');
      }
      
      if (cameraAction === 'punchin') {
        
        // Create new attendance record with all required fields
        const attendanceData = {
          company: companyId,
          employee: employeeId,
          date: today,
          inTime: now.toISOString(),
          inLocation: 'Office',
          inPhoto: compressedImage,
          status: 'Present',
          workingTime: 0
        };
        
        
        try {
          const savedAttendance = await createAttendance(attendanceData);
          
          // Update state with the saved attendance
          setCurrentAttendance(savedAttendance);
          setPunchInTime(new Date(savedAttendance.inTime));
          setAttendanceStatus('punched-in');
          setCapturedPunchInImage(compressedImage);
          
          // Show success message
          setNotification({
            show: true,
            message: 'Punched in successfully!',
            type: 'success'
          });
          
          
        } catch (error) {
          
          setNotification({
            show: true,
            message: error.message || 'Failed to save punch in. Please try again.',
            type: 'error'
          });
          
          throw error;
        }
        
      } else if (cameraAction === 'punchout') {
        
        // Get existing attendance record
        let existingAttendance = currentAttendance;
        if (!existingAttendance) {
          try {
            existingAttendance = await fetchTodaysAttendance();
          } catch (fetchError) {
          }
        }
        
        if (!existingAttendance || !existingAttendance._id) {
          setNotification({
            show: true,
            message: 'No punch-in record found. Please punch in first.',
            type: 'error'
          });
          return null;
        }
        
        setCapturedPunchOutImage(compressedImage);
        
        // Calculate working time in minutes
        const punchInTime = existingAttendance.inTime ? new Date(existingAttendance.inTime) : new Date();
        const workingTimeMinutes = Math.round((now - punchInTime) / (1000 * 60));
        
        // Prepare update data for punch out
        const updateData = {
          outTime: now.toISOString(),
          outLocation: 'Office',
          outPhoto: compressedImage,
          status: 'Completed',
          workingTime: workingTimeMinutes
        };
        
        
        try {
          const savedAttendance = await updateAttendance(existingAttendance._id, updateData);
          
          // Update state with the saved attendance
          const updatedAttendance = { ...existingAttendance, ...savedAttendance };
          setCurrentAttendance(updatedAttendance);
          setPunchOutTime(new Date(updatedAttendance.outTime));
          setWorkHours(calculateWorkHours(
            new Date(updatedAttendance.inTime).toTimeString().substring(0, 5),
            new Date(updatedAttendance.outTime).toTimeString().substring(0, 5)
          ));
          setAttendanceStatus('completed');
          
          // Show success message
          setNotification({
            show: true,
            message: 'Punched out successfully!',
            type: 'success'
          });
          
          
        } catch (error) {
          setNotification({
            show: true,
            message: error.message || 'Failed to save punch out. Please try again.',
            type: 'error'
          });
          throw error;
        }
      }
      
      // Stop camera and close modal
      stopCamera();
      setShowCamera(false);
      
    } catch (error) {
      
      // Stop camera and close modal on error
      stopCamera();
      setShowCamera(false);
      
      // Show error notification if not already shown
      if (!error.message?.includes('Failed to')) {
        setNotification({
          show: true,
          message: `Failed to process attendance: ${error.message || 'Please try again.'}`,
          type: 'error'
        });
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Present': return 'bg-green-100 text-green-800';
      case 'Late': return 'bg-yellow-100 text-yellow-800';
      case 'Absent': return 'bg-red-100 text-red-800';
      case 'Half-day': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusDotColor = (status) => {
    switch (status) {
      case 'Present': return 'bg-green-500';
      case 'Late': return 'bg-yellow-500';
      case 'Absent': return 'bg-red-500';
      case 'Half-day': return 'bg-orange-500';
      default: return 'bg-gray-500';
    }
  };

  // Validate attendance data before sending to API
  const validateAttendanceData = (data) => {
    const requiredFields = ['company', 'employee', 'date', 'inTime'];
    const missingFields = requiredFields.filter(field => !data[field]);
    
    if (missingFields.length > 0) {
      const error = new Error(`Missing required fields: ${missingFields.join(', ')}`);
      error.status = 400;
      throw error;
    }
    
    // Validate ObjectId format for company and employee
    if (data.company && !data.company.match(/^[0-9a-fA-F]{24}$/)) {
      throw new Error('Invalid company ID format');
    }
    
    if (data.employee && !data.employee.match(/^[0-9a-fA-F]{24}$/)) {
      throw new Error('Invalid employee ID format');
    }
    
    // Validate date format
    if (data.date && isNaN(new Date(data.date).getTime())) {
      throw new Error('Invalid date format');
    }
    
    return true;
  };

  const createAttendance = async (attendanceData) => {
    
    const token = localStorage.getItem("authToken");
    const employeeId = localStorage.getItem("selectedEmployeeId");
    const today = new Date().toISOString().split('T')[0];
    const API_BASE_URL = 'https://customersupport-jhy9.onrender.com/api/attendance';
    const LOCAL_STORAGE_KEY = `attendance_${employeeId}_${today}`;

    if (!token) {
      throw new Error('No authentication token found. Please log in.');
    }

    // Validate the data before sending
    validateAttendanceData(attendanceData);

    // Save to local storage first as backup
    const localAttendanceData = {
      ...attendanceData,
      _id: `local_${Date.now()}`, // Temporary local ID
      _isLocal: true,
      _createdAt: new Date().toISOString()
    };
    
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(localAttendanceData));
    } catch (localError) {
    }

    const config = {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json'
      },
      timeout: 15000
    };

    try {
      const response = await axios.post(API_BASE_URL, attendanceData, config);
      
      // Update local storage with server response
      try {
        const serverData = { ...response.data, _synced: true, _syncedAt: new Date().toISOString() };
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(serverData));
      } catch (localError) {
      }
      
      return response.data;
    } catch (error) {

      // For 500 errors, return the local data and show a warning
      if (error.response?.status === 500) {
        setNotification({
          show: true,
          message: 'Attendance saved locally. Will sync when server is available.',
          type: 'warning',
          autoHide: true
        });
        return localAttendanceData;
      }

      let errorMessage = 'Failed to create attendance record. ';
      if (error.response?.status === 401) {
        errorMessage = 'Session expired. Please log in again.';
      } else if (error.response?.status === 400) {
        errorMessage = error.response?.data?.message || 'Invalid attendance data provided.';
      } else if (error.response?.data?.message) {
        errorMessage += error.response.data.message;
      } else {
        errorMessage += error.message;
      }

      throw new Error(errorMessage);
    }
  };

  const updateAttendance = async (attendanceId, attendanceData) => {
    
    const token = localStorage.getItem("authToken");
    const API_BASE_URL = 'https://customersupport-jhy9.onrender.com/api/attendance';

    if (!token) {
      throw new Error('No authentication token found. Please log in.');
    }

    if (!attendanceId) {
      throw new Error('Attendance ID is required for updates.');
    }

    const config = {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json'
      },
      timeout: 15000
    };

    try {
      const response = await axios.put(`${API_BASE_URL}/${attendanceId}`, attendanceData, config);
      return response.data;
    } catch (error) {

      let errorMessage = 'Failed to update attendance record. ';
      if (error.response?.status === 401) {
        errorMessage = 'Session expired. Please log in again.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Attendance record not found.';
      } else if (error.response?.status === 400) {
        errorMessage = error.response?.data?.message || 'Invalid attendance data provided.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again later or contact support.';
      } else if (error.response?.data?.message) {
        errorMessage += error.response.data.message;
      } else {
        errorMessage += error.message;
      }

      throw new Error(errorMessage);
    }
  };

  // Fetch attendance by specific date using the centralized function
  const fetchAttendanceByDate = async (targetDate) => {
    console.log('Fetching attendance for date:', targetDate);
    
    if (!employee || !employee._id) {
      throw new Error('Employee information not available. Please refresh the page.');
    }

    try {
      // Use the centralized function to get all records
      const allRecords = await fetchAllAttendanceRecords();
      console.log("All attendance records fetched:", allRecords);
      
      // Find the record for the specific date
      const dateRecord = allRecords.find(record => record.date === targetDate);
      
      if (dateRecord) {
        return dateRecord;
      }
      
      // No record found for this date
      return null;
      
    } catch (error) {
      // Re-throw the error with context
      throw new Error(`Failed to fetch attendance for ${targetDate}: ${error.message}`);
    }
  };

  // Fetch today's attendance from the database
  const fetchTodaysAttendance = async () => {
    const token = localStorage.getItem("authToken");
    const today = new Date().toISOString().split('T')[0];
    const API_BASE_URL = 'https://customersupport-jhy9.onrender.com/api/attendance';
    
    if (!token) {
      throw new Error('No access token found');
    }

    // Get IDs from token and employee data
    let companyId, employeeId;
    try {
      const decoded = jwtDecode(token);
      
      employeeId = decoded.employeeId || decoded.id || decoded._id || decoded.userId;
      
      // Prioritize employee data for companyId since JWT doesn't contain it
      if (employee?.company) {
        companyId = employee.company;
      } else {
        // Fallback to token (though it likely won't have companyId)
        companyId = decoded.companyId || 
                    decoded.company?._id || 
                    decoded.company?.id || 
                    decoded.company || 
                    decoded.orgId || 
                    decoded.organizationId;
      }
      
    } catch (err) {
      throw new Error('Invalid token format');
    }

    if (!employeeId) {
      throw new Error('Employee ID not found in token');
    }

    if (!companyId) {
    }
    
    const LOCAL_STORAGE_KEY = `attendance_${employeeId}_${today}`;

    // First check local storage
    try {
      const localData = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (localData) {
        const parsedData = JSON.parse(localData);
        // If we have local data, try to sync with server in background
        if (parsedData._isLocal) {
        }
        return parsedData;
      }
    } catch (localError) {
    }

    const config = {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      timeout: 10000
    };


    try {
      // Get all attendance records and filter on client side for now
      // In production, you might want to add query parameters to the API
      const response = await axios.get(API_BASE_URL, config);
      

      // Handle different response formats
      let attendanceRecords = [];
      if (Array.isArray(response.data)) {
        attendanceRecords = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        attendanceRecords = response.data.data;
      } else if (response.data?.data) {
        attendanceRecords = [response.data.data];
      } else if (response.data) {
        attendanceRecords = [response.data];
      }

      // Filter for today's attendance for the current employee and company
      const todaysAttendance = attendanceRecords.find(record => {
        const recordDate = record.date ? new Date(record.date).toISOString().split('T')[0] : null;
        return recordDate === today && 
               record.employee === employeeId && 
               record.company === companyId;
      });

      // Cache the result in local storage
      if (todaysAttendance) {
        try {
          const cacheData = { ...todaysAttendance, _synced: true, _cachedAt: new Date().toISOString() };
          localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(cacheData));
        } catch (cacheError) {
        }
      }

      return todaysAttendance || null;
      
    } catch (error) {

      // For 500 errors, try to use local storage as fallback
      if (error.response?.status === 500) {
        try {
          const localData = localStorage.getItem(LOCAL_STORAGE_KEY);
          if (localData) {
            const parsedData = JSON.parse(localData);
            return parsedData;
          }
        } catch (localError) {
        }
        return null;
      }

      let errorMessage = 'Failed to load attendance data.';
      
      if (error.response?.status === 401) {
        errorMessage = 'Session expired. Please log in again.';
      } else if (error.response?.status === 404) {
        errorMessage = 'No attendance records found.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setNotification({
        show: true,
        message: errorMessage,
        type: 'error',
        autoHide: true
      });

      return null;
    }
  };

  // Helper function to check for locally stored attendance as fallback
  const checkLocalAttendance = (companyId, employeeId, date) => {
    try {
      // Get all attendance keys from local storage
      const keys = Object.keys(localStorage).filter(key => key.startsWith('attendance_'));
      
      // Try to find a matching attendance record
      for (const key of keys) {
        try {
          const localData = JSON.parse(localStorage.getItem(key));
          
          // Skip if data is not an object or doesn't have required fields
          if (!localData || typeof localData !== 'object') continue;
          
          // Parse the date from the record
          const recordDate = localData.date 
            ? new Date(localData.date).toISOString().split('T')[0]
            : null;
          
          // Check if this record matches our criteria
          if (recordDate === date && 
              localData.company === companyId && 
              localData.employee === employeeId) {
            return localData;
          }
        } catch (parseError) {
          continue;
        }
      }
      
      return null;
    } catch (error) {
      return null;
    }
  };

  const loadTodaysAttendance = async () => {
    // Only load if employee data is available
    if (!employee || !employee._id) {
      return;
    }
    
    try {
      const attendance = await fetchTodaysAttendance();
      
      if (!attendance) {
        // Initialize with default values
        setPunchInTime(null);
        setPunchOutTime(null);
        setWorkHours('00:00');
        setAttendanceStatus('not-started');
        setCurrentAttendance(null);
        return null;
      }

      // Set the current attendance record
      setCurrentAttendance(attendance);

      // Ensure we have valid date objects
      const inTime = attendance.inTime ? new Date(attendance.inTime) : null;
      const outTime = attendance.outTime ? new Date(attendance.outTime) : null;

      // Validate dates
      if (inTime && isNaN(inTime.getTime())) {
        throw new Error('Invalid punch-in time format');
      }
      if (outTime && isNaN(outTime.getTime())) {
        throw new Error('Invalid punch-out time format');
      }

      setPunchInTime(inTime);
      
      // Set captured images if available
      if (attendance.inPhoto) {
        setCapturedPunchInImage(attendance.inPhoto);
      }
      if (attendance.outPhoto) {
        setCapturedPunchOutImage(attendance.outPhoto);
      }
      
      if (outTime) {
        // If already punched out
        setPunchOutTime(outTime);
        // Convert working time from minutes to HH:MM format
        const workingMinutes = attendance.workingTime || 0;
        const hours = Math.floor(workingMinutes / 60);
        const minutes = workingMinutes % 60;
        setWorkHours(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`);
        setAttendanceStatus('completed');
      } else if (inTime) {
        // If only punched in
        setAttendanceStatus('punched-in');
        
        // Calculate current work hours
        const now = new Date();
        const diffMs = now - inTime;
        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        setWorkHours(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`);
      } else {
        // No valid times
        setAttendanceStatus('not-started');
      }
    } catch (error) {
      // Reset to default state on error
      setPunchInTime(null);
      setPunchOutTime(null);
      setWorkHours('00:00');
      setAttendanceStatus('not-started');
      setCurrentAttendance(null);
    }
  };

  useEffect(() => {
    loadTodaysAttendance();
  }, [employee?._id]); // Load when employee data is available

  // Auto-hide notifications after 5 seconds
  useEffect(() => {
    if (notification.show && notification.autoHide !== false) {
      const timer = setTimeout(() => {
        setNotification({ ...notification, show: false });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification.show]);

  const viewImage = (imageSrc, title) => {
    const newWindow = window.open();
    newWindow.document.write(`
      <html>
        <head><title>${title}</title></head>
        <body style="margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #f3f4f6;">
          <img src="${imageSrc}" style="max-width: 100%; max-height: 100vh;" />
        </body>
      </html>
    `);
  };

  const openAttendanceDetails = async (date, record) => {
    setSelectedDate(date);
    
    // 🎯 CHECK FOR COMPANY HOLIDAY FIRST
    const holidayForDate = holidays.find(holiday => holiday.date === date);
    const isHolidayDate = !!holidayForDate;
    
    console.log('📅 OPENING ATTENDANCE DETAILS:', {
      date: date,
      isHolidayDate: isHolidayDate,
      holidayInfo: holidayForDate,
      hasExistingRecord: !!record
    });
    
    if (isHolidayDate) {
      // 🎉 HOLIDAY DATE - Show holiday information instead of attendance
      console.log('🎉 DISPLAYING HOLIDAY DETAILS:', holidayForDate);
      
      setSelectedRecord({
        date: date,
        isHoliday: true,
        holidayInfo: holidayForDate,
        status: 'Holiday',
        loading: false,
        error: false,
        noData: false
      });
      setTempReport('');
      setIsModalOpen(true);
      return;
    }
    
    // Always show loading state first for non-holiday dates
    setSelectedRecord({
      loading: true,
      date: date,
      status: 'Loading...',
      punchIn: '-',
      punchOut: '-',
      hours: '--:--',
      report: '',
      isHoliday: false
    });
    setIsModalOpen(true);
    
    try {
      // Always fetch fresh data from server for accurate information
      const fetchedRecord = await fetchAttendanceByDate(date);
      console.log("fetchedRecord",fetchedRecord);
      
      if (fetchedRecord) {
        // Found attendance record
        setSelectedRecord({
          ...fetchedRecord,
          loading: false,
          error: false,
          noData: false,
          isHoliday: false
        });
        setTempReport(fetchedRecord.report || '');
        
        // Update attendance history with fresh data
        const existingIndex = attendanceHistory.findIndex(r => r.date === date);
        if (existingIndex === -1) {
          // Add new record
          setAttendanceHistory(prev => [fetchedRecord, ...prev].sort((a, b) => new Date(b.date) - new Date(a.date)));
        } else {
          // Update existing record
          setAttendanceHistory(prev => {
            const updated = [...prev];
            updated[existingIndex] = fetchedRecord;
            return updated.sort((a, b) => new Date(b.date) - new Date(a.date));
          });
        }
      } else {
        // No attendance record found for this date
        setSelectedRecord({
          date: date,
          status: 'Absent',
          punchIn: '-',
          punchOut: '-',
          hours: '--:--',
          report: '',
          loading: false,
          error: false,
          noData: true,
          isHoliday: false
        });
        setTempReport('');
      }
    } catch (error) {
      // Error occurred while fetching
      setSelectedRecord({
        date: date,
        status: 'Error',
        punchIn: '-',
        punchOut: '-',
        hours: '--:--',
        report: '',
        loading: false,
        error: true,
        noData: false,
        isHoliday: false,
        errorMessage: error.message || 'Failed to load attendance data'
      });
      setTempReport('');
      
      // Show error notification
      setNotification({
        show: true,
        message: `Failed to load attendance for ${new Date(date).toLocaleDateString()}: ${error.message}`,
        type: 'error',
        autoHide: true
      });
    }
  };

  const saveReport = () => {
    if (!selectedRecord) return;
    
    const updatedHistory = attendanceHistory.map(record => {
      if (record.date === selectedRecord.date) {
        return { ...record, report: tempReport };
      }
      return record;
    });
    
    setAttendanceHistory(updatedHistory);
    setEditingReport(false);
    setSelectedRecord(prev => ({ ...prev, report: tempReport }));
  };

  const years = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);
  
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const generateCalendar = () => {
    const firstDay = new Date(selectedYear, selectedMonth, 1);
    const lastDay = new Date(selectedYear, selectedMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    const today = new Date();
    const isCurrentMonth = today.getFullYear() === selectedYear && today.getMonth() === selectedMonth;
    
    // Get employee joining date
    let joiningDate = null;
    if (employee && employee.dateOfJoining) {
      joiningDate = new Date(employee.dateOfJoining);
    } else if (employee && employee.joiningDate) {
      joiningDate = new Date(employee.joiningDate);
    } else if (employee && employee.joinDate) {
      joiningDate = new Date(employee.joinDate);
    }
    
    // Get company weekly holidays
    let weeklyHolidays = [];
    if (selectedCompany && selectedCompany.weeklyHoliday) {
      weeklyHolidays = Array.isArray(selectedCompany.weeklyHoliday) 
        ? selectedCompany.weeklyHoliday 
        : [selectedCompany.weeklyHoliday];
    } else if (selectedCompany && selectedCompany.weeklyHolidays) {
      weeklyHolidays = Array.isArray(selectedCompany.weeklyHolidays) 
        ? selectedCompany.weeklyHolidays 
        : [selectedCompany.weeklyHolidays];
    }
    
    // Convert day names to day numbers (0 = Sunday, 1 = Monday, etc.)
    const dayNameToNumber = {
      'sunday': 0, 'sun': 0,
      'monday': 1, 'mon': 1,
      'tuesday': 2, 'tue': 2, 'tues': 2,
      'wednesday': 3, 'wed': 3,
      'thursday': 4, 'thu': 4, 'thur': 4, 'thurs': 4,
      'friday': 5, 'fri': 5,
      'saturday': 6, 'sat': 6
    };
    
    const holidayDayNumbers = weeklyHolidays.map(day => 
      dayNameToNumber[day.toLowerCase()] !== undefined ? dayNameToNumber[day.toLowerCase()] : -1
    ).filter(num => num !== -1);
    
    // 🎯 HOLIDAY INTEGRATION: Create holiday lookup for current month
    const monthHolidays = holidays.filter(holiday => {
      const holidayDate = new Date(holiday.date);
      return holidayDate.getFullYear() === selectedYear && holidayDate.getMonth() === selectedMonth;
    });
    
    console.log('📅 HOLIDAYS FOR CURRENT MONTH:', {
      selectedMonth: selectedMonth + 1,
      selectedYear: selectedYear,
      totalHolidays: holidays.length,
      monthHolidays: monthHolidays.length,
      holidays: monthHolidays.map(h => ({ name: h.name, date: h.date, type: h.type }))
    });
    
    const calendar = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDay; i++) {
      calendar.push({ day: null, data: null, isEmpty: true });
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${selectedYear}-${(selectedMonth + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      const isToday = isCurrentMonth && day === today.getDate();
      const isPastDate = new Date(dateStr) < today && !isToday;
      
      // Check if this day is a weekly holiday
      const currentDate = new Date(dateStr);
      const dayOfWeek = currentDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
      const isWeeklyHoliday = holidayDayNumbers.includes(dayOfWeek);
      
      // 🎯 CHECK FOR COMPANY HOLIDAYS
      const holidayForThisDate = monthHolidays.find(holiday => holiday.date === dateStr);
      const isCompanyHoliday = !!holidayForThisDate;
      
      // Find attendance record for this date from the centralized attendance history
      const attendanceRecord = attendanceHistory.find(record => record.date === dateStr);
      
      if (isToday && !attendanceRecord) {
        // For today, show current status even if no historical record
        let status = 'Absent';
        if (attendanceStatus === 'completed') {
          // Determine status based on work hours
          const [hours] = workHours.split(':').map(Number);
          status = hours < 4 ? 'Half-day' : 'Present';
        } else if (attendanceStatus === 'punched-in') {
          status = 'Incomplete';
        }
        
        const formatTime = (time) => {
          if (!time) return '-';
          try {
            const date = time instanceof Date ? time : new Date(time);
            const time24 = isNaN(date.getTime()) ? '-' : date.toTimeString().substring(0, 5);
            return formatTo12Hour(time24);
          } catch (e) {
            return '-';
          }
        };

        const todayStatus = {
          date: dateStr,
          status: status,
          punchIn: formatTime(punchInTime),
          punchOut: formatTime(punchOutTime),
          hours: workHours,
          report: workReport,
          punchInImage: capturedPunchInImage,
          punchOutImage: capturedPunchOutImage,
          inLocation: 'Office',
          outLocation: 'Office'
        };
        calendar.push({ day, data: todayStatus, isToday: true, isPastDate: false, isWeeklyHoliday, isCompanyHoliday, holidayInfo: holidayForThisDate });
      } else if (!attendanceRecord && isPastDate) {
        // Check if this date is after joining date
        const currentDate = new Date(dateStr);
        const isAfterJoining = !joiningDate || currentDate >= joiningDate;
        
        if (isAfterJoining && !isWeeklyHoliday && !isCompanyHoliday) {
          // For past dates without attendance records (after joining, excluding weekly holidays and company holidays), mark as absent
          const absentRecord = {
            date: dateStr,
            status: 'Absent',
            punchIn: '-',
            punchOut: '-',
            hours: '--:--',
            report: '',
            punchInImage: null,
            punchOutImage: null,
            inLocation: '-',
            outLocation: '-'
          };
          calendar.push({ day, data: absentRecord, isToday: false, isPastDate: true, isAbsent: true, isWeeklyHoliday, isCompanyHoliday, holidayInfo: holidayForThisDate });
        } else {
          // For dates before joining, weekly holidays, or company holidays, show as normal calendar day without absence marking
          calendar.push({ day, data: null, isToday: false, isPastDate: true, isAbsent: false, isBeforeJoining: !isAfterJoining, isWeeklyHoliday, isCompanyHoliday, holidayInfo: holidayForThisDate });
        }
      } else {
        calendar.push({ day, data: attendanceRecord, isToday: isToday, isPastDate: isPastDate, isAbsent: false, isWeeklyHoliday, isCompanyHoliday, holidayInfo: holidayForThisDate });
      }
    }
    
    return calendar;
  };

  const calendarDays = generateCalendar();
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Calculate monthly summary statistics
  const calculateMonthlySummary = () => {
    const currentMonthRecords = filteredHistory;
    const today = new Date();
    const currentMonth = selectedMonth;
    const currentYear = selectedYear;
    
    // Get total days in the selected month
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    
    let totalWorkingDays = 0;
    let totalHalfDays = 0;
    let totalLeaves = 0;
    let totalPresent = 0;
    let totalLate = 0;
    let totalIncomplete = 0;
    let totalFullDayWorking = 0; // New counter for full day working

    // Count attendance records
    currentMonthRecords.forEach(record => {
      // Calculate if it's a full day work (8+ hours)
      let isFullDay = false;
      if (record.hours && record.hours !== '--:--' && record.hours !== 'Ongoing') {
        const [hours, minutes] = record.hours.split(':').map(Number);
        const totalHours = hours + (minutes / 60);
        isFullDay = totalHours >= 8; // Consider 8+ hours as full day
      }

      switch (record.status) {
        case 'Present':
          totalPresent++;
          totalWorkingDays++;
          if (isFullDay) totalFullDayWorking++;
          break;
        case 'Late':
          totalLate++;
          totalWorkingDays++;
          if (isFullDay) totalFullDayWorking++;
          break;
        case 'Half-day':
          totalHalfDays++;
          totalWorkingDays++;
          // Half-day typically won't be full day, but check anyway
          if (isFullDay) totalFullDayWorking++;
          break;
        case 'Absent':
          totalLeaves++;
          break;
        case 'Incomplete':
          totalIncomplete++;
          totalWorkingDays++;
          if (isFullDay) totalFullDayWorking++;
          break;
        default:
          break;
      }
    });

    // Count absent days (past dates without attendance records, but only after dateOfJoining and excluding weekly holidays)
    let joiningDate = null;
    if (employee && employee.dateOfJoining) {
      joiningDate = new Date(employee.dateOfJoining);
    } else if (employee && employee.joiningDate) {
      joiningDate = new Date(employee.joiningDate);
    } else if (employee && employee.joinDate) {
      joiningDate = new Date(employee.joinDate);
    }

    // Get company weekly holidays for summary calculation
    let weeklyHolidays = [];
    if (selectedCompany && selectedCompany.weeklyHoliday) {
      weeklyHolidays = Array.isArray(selectedCompany.weeklyHoliday) 
        ? selectedCompany.weeklyHoliday 
        : [selectedCompany.weeklyHoliday];
    } else if (selectedCompany && selectedCompany.weeklyHolidays) {
      weeklyHolidays = Array.isArray(selectedCompany.weeklyHolidays) 
        ? selectedCompany.weeklyHolidays 
        : [selectedCompany.weeklyHolidays];
    }
    
    // Convert day names to day numbers for summary calculation
    const dayNameToNumber = {
      'sunday': 0, 'sun': 0,
      'monday': 1, 'mon': 1,
      'tuesday': 2, 'tue': 2, 'tues': 2,
      'wednesday': 3, 'wed': 3,
      'thursday': 4, 'thu': 4, 'thur': 4, 'thurs': 4,
      'friday': 5, 'fri': 5,
      'saturday': 6, 'sat': 6
    };
    
    const holidayDayNumbers = weeklyHolidays.map(day => 
      dayNameToNumber[day.toLowerCase()] !== undefined ? dayNameToNumber[day.toLowerCase()] : -1
    ).filter(num => num !== -1);

    // 🎯 HOLIDAY INTEGRATION: Get holidays for summary calculation
    const summaryMonthHolidays = holidays.filter(holiday => {
      const holidayDate = new Date(holiday.date);
      return holidayDate.getFullYear() === currentYear && holidayDate.getMonth() === currentMonth;
    });

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${currentYear}-${(currentMonth + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      const dateObj = new Date(dateStr);
      const isPastDate = dateObj < today;
      const isToday = dateObj.toDateString() === today.toDateString();
      
      // Check if this day is a weekly holiday
      const dayOfWeek = dateObj.getDay();
      const isWeeklyHoliday = holidayDayNumbers.includes(dayOfWeek);
      
      // Check if this day is a company holiday
      const isCompanyHoliday = summaryMonthHolidays.some(holiday => holiday.date === dateStr);
      
      // Only count past dates (not future dates or today) and only after joining date, excluding weekly holidays and company holidays
      const isAfterJoining = !joiningDate || dateObj >= joiningDate;
      
      if (isPastDate && !isToday && isAfterJoining && !isWeeklyHoliday && !isCompanyHoliday) {
        const hasRecord = currentMonthRecords.some(record => record.date === dateStr);
        if (!hasRecord) {
          totalLeaves++; // Count as absent/leave day (excluding weekly holidays and company holidays)
        }
      }
    }

    return {
      totalWorkingDays,
      totalHalfDays,
      totalLeaves,
      totalPresent,
      totalLate,
      totalIncomplete,
      totalFullDayWorking,
      totalCompanyHolidays: summaryMonthHolidays.length, // 🎉 Add holiday count
      totalRecords: currentMonthRecords.length,
      totalDaysInMonth: daysInMonth
    };
  };

  const monthlySummary = calculateMonthlySummary();

  const renderWorkHours = () => {
    if (attendanceStatus === 'not-started') {
      return '00:00';
    }
    
    if (attendanceStatus === 'completed' && punchInTime && punchOutTime) {
      // Calculate final work hours if not already done
      if (workHours === '00:00') {
        const diffMs = punchOutTime - punchInTime;
        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      }
      return workHours;
    }
    
    if (punchInTime) {
      return workHours; // This will be updated by the effect
    }
    
    return '00:00';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <header className="mb-8">
          {/* Enhanced Header with Better UI */}
          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 border border-gray-100">
            <div className="text-center">
              {/* Loading State */}
              {loading && (
                <div className="flex items-center justify-center space-x-2 mb-4">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                  <span className="text-indigo-600 font-medium">Loading employee data...</span>
                </div>
              )}
              
              {/* Error State */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-center space-x-2">
                    <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span className="text-red-700 font-medium">{error}</span>
                  </div>
                </div>
              )}
              
              
              {/* Main Title */}
              <div className="mb-4">
                <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-2">
                  Daily Attendance System
                </h1>
                <p className="text-gray-600 text-lg">Track your work hours and submit daily reports</p>
              </div>
              
              {/* Date, Time, Team Member & Shift Display */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-100">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-center">
                  {/* Date */}
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <div className="flex items-center justify-center space-x-2 mb-1">
                      <svg className="w-4 h-4 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                      </svg>
                      <span className="text-xs font-medium text-gray-600">Today</span>
                    </div>
                    <div className="text-sm font-semibold text-gray-800">
                      {currentTime.toLocaleDateString('en-US', { 
                        weekday: 'short', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </div>
                  </div>
                  
                  {/* Current Time */}
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <div className="flex items-center justify-center space-x-2 mb-1">
                      <svg className="w-4 h-4 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                      </svg>
                      <span className="text-xs font-medium text-gray-600">Time</span>
                    </div>
                    <div className="text-sm font-bold text-indigo-600">
                      {currentTime.toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                  
                  {/* Team Member Name */}
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <div className="flex items-center justify-center space-x-2 mb-1">
                      <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                      </svg>
                      <span className="text-xs font-medium text-gray-600">Team Member</span>
                    </div>
                    <div className="text-sm font-bold text-green-600">
                      {employee ? (
                        employee.teamMemberName || 
                        employee.fullName || 
                        employee.name || 
                        `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || 
                        'Loading...'
                      ) : 'Loading...'}
                    </div>
                  </div>
                  
                  {/* Shift Information */}
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <div className="flex items-center justify-center space-x-2 mb-1">
                      <svg className="w-4 h-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                      </svg>
                      <span className="text-xs font-medium text-gray-600">Shift</span>
                    </div>
                    <div className="text-sm font-bold text-purple-600">
                      {employeeShift ? (
                        `${formatTo12Hour(employeeShift.startTime)} - ${formatTo12Hour(employeeShift.endTime)}`
                      ) : (
                        employee ? 'Loading...' : '--:-- - --:--'
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Monthly Summary Cards */}
        <div className="mb-8">
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Monthly Summary - {months[selectedMonth]} {selectedYear}
              </h2>
              <div className="text-sm text-gray-500">
                Total Records: {monthlySummary.totalRecords}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              {/* Total Working Days Card */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-green-700">
                      {monthlySummary.totalWorkingDays}
                    </div>
                    <div className="text-sm text-green-600 font-medium">
                      Days
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-green-800">Total Working Days</h3>
                  <p className="text-sm text-green-600">
                    Includes Present ({monthlySummary.totalPresent}), Late ({monthlySummary.totalLate}), and Incomplete ({monthlySummary.totalIncomplete}) days
                  </p>
                  <div className="flex items-center justify-between text-xs text-green-700 bg-green-100 rounded-lg px-3 py-2">
                    <span>Present: {monthlySummary.totalPresent}</span>
                    <span>Late: {monthlySummary.totalLate}</span>
                  </div>
                </div>
              </div>

              {/* Total Half Days Card */}
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-6 border border-orange-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-orange-100 rounded-lg">
                    <svg className="w-6 h-6 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-orange-700">
                      {monthlySummary.totalHalfDays}
                    </div>
                    <div className="text-sm text-orange-600 font-medium">
                      Days
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-orange-800">Total Half Days</h3>
                  <p className="text-sm text-orange-600">
                    Days with less than 4 hours of work
                  </p>
                  <div className="flex items-center justify-center text-xs text-orange-700 bg-orange-100 rounded-lg px-3 py-2">
                    <span>Work Hours &lt; 4 hours</span>
                  </div>
                </div>
              </div>

              {/* Total Leaves Card */}
              <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-xl p-6 border border-red-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-red-100 rounded-lg">
                    <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-red-700">
                      {monthlySummary.totalLeaves}
                    </div>
                    <div className="text-sm text-red-600 font-medium">
                      Days
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-red-800">Total Leaves</h3>
                  <p className="text-sm text-red-600">
                    Days marked as absent (excluding company holidays)
                  </p>
                  <div className="flex items-center justify-center text-xs text-red-700 bg-red-100 rounded-lg px-3 py-2">
                    <span>Excludes Holidays</span>
                  </div>
                </div>
              </div>

              {/* Full Day Working Card */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-blue-700">
                      {monthlySummary.totalFullDayWorking}
                    </div>
                    <div className="text-sm text-blue-600 font-medium">
                      Days
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-blue-800">Full Day Working</h3>
                  <p className="text-sm text-blue-600">
                    Days with 8+ hours of work completed
                  </p>
                  <div className="flex items-center justify-center text-xs text-blue-700 bg-blue-100 rounded-lg px-3 py-2">
                    <span>Work Hours ≥ 8 hours</span>
                  </div>
                </div>
              </div>

              {/* 🎉 Company Holidays Card */}
              <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-6 border border-purple-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <svg className="w-6 h-6 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-purple-700">
                      {monthlySummary.totalCompanyHolidays}
                    </div>
                    <div className="text-sm text-purple-600 font-medium">
                      Days
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-purple-800">Company Holidays</h3>
                  <p className="text-sm text-purple-600">
                    Official company holidays and festivals
                  </p>
                  <div className="flex items-center justify-center text-xs text-purple-700 bg-purple-100 rounded-lg px-3 py-2">
                    <span>🎉 No Attendance Required</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Statistics Row */}
            {/* <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-indigo-600">
                    {monthlySummary.totalRecords > 0 ? 
                      Math.round((monthlySummary.totalWorkingDays / monthlySummary.totalRecords) * 100) : 0}%
                  </div>
                  <div className="text-sm text-gray-600">Attendance Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {monthlySummary.totalPresent}
                  </div>
                  <div className="text-sm text-gray-600">On Time Days</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-600">
                    {monthlySummary.totalLate}
                  </div>
                  <div className="text-sm text-gray-600">Late Days</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">
                    {monthlySummary.totalIncomplete}
                  </div>
                  <div className="text-sm text-gray-600">Incomplete Days</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {monthlySummary.totalFullDayWorking}
                  </div>
                  <div className="text-sm text-gray-600">Full Day Working</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {monthlySummary.totalCompanyHolidays}
                  </div>
                  <div className="text-sm text-gray-600">Company Holidays</div>
                </div>
              </div>
            </div> */}
          </div>
        </div>

        {showCamera && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full">
              <h2 className="text-xl font-semibold text-gray-800 mb-4 text-center">
                {cameraAction === 'punchin' ? 'Punch In Verification' : 'Punch Out Verification'}
              </h2>
              <p className="text-gray-600 mb-4 text-center">Please position your face in the frame</p>
              
              <div className="relative bg-gray-200 rounded-lg overflow-hidden mb-4">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  className="w-full h-64 object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="border-2 border-white border-dashed rounded-full w-48 h-48"></div>
                </div>
              </div>
              
              <div className="flex justify-center space-x-4">
                <button
                  onClick={captureImage}
                  className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors"
                >
                  Capture
                </button>
                <button
                  onClick={() => {
                    setShowCamera(false);
                    setCameraAction('');
                  }}
                  className="px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white font-semibold rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-center">
          {/* Attendance History Card */}
          <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-4xl">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                📅 Attendance History
              </h2>
              <p className="text-gray-600 mt-2">View your monthly attendance records and calendar</p>
            </div>
            
            <div className="mb-4 flex border-b border-gray-200">
              <button 
                onClick={() => setViewMode('calendar')}
                className={`px-3 py-2 font-medium ${viewMode === 'calendar' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-indigo-600'}`}
              >
                Calendar View
              </button>

            </div>
            
            {/* Year and Month Selector */}
            <div className="mb-6 flex justify-center">
              <div className="grid grid-cols-2 gap-4 w-full max-w-md">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-center">📅 Year</label>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white shadow-sm text-center font-medium"
                  >
                    {years.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-center">🗓️ Month</label>
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white shadow-sm text-center font-medium"
                  >
                    {months.map((month, index) => (
                      <option key={index} value={index}>{month}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            
            {viewMode === 'calendar' ? (
              <div className="overflow-y-auto max-h-96">
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {dayNames.map(day => (
                    <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
                      {day}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {calendarDays.map(({ day, data, isToday, isPastDate, isAbsent, isEmpty, isBeforeJoining, isWeeklyHoliday, isCompanyHoliday, holidayInfo }, index) => (
                    <div
                      key={index}
                      className={`min-h-12 p-1 border rounded-md text-center transition-all duration-200 ${
                        day ? 'cursor-pointer hover:shadow-md' : 'bg-gray-50'
                      } ${
                        // Background colors based on status (Company holidays take priority)
                        isEmpty ? 'bg-gray-50' :
                        isCompanyHoliday ? 'bg-purple-100 hover:bg-purple-150 border-purple-300' :
                        isWeeklyHoliday ? 'bg-gray-200 hover:bg-gray-250 border-gray-400' :
                        isBeforeJoining ? 'bg-gray-100 hover:bg-gray-150 border-gray-300' :
                        isAbsent || (data && data.status === 'Absent') ? 'bg-red-50 hover:bg-red-100 border-red-200' :
                        data && data.status === 'Present' ? 'bg-green-50 hover:bg-green-100 border-green-200' :
                        data && data.status === 'Late' ? 'bg-yellow-50 hover:bg-yellow-100 border-yellow-200' :
                        data && data.status === 'Half-day' ? 'bg-orange-50 hover:bg-orange-100 border-orange-200' :
                        data && data.status === 'Incomplete' ? 'bg-blue-50 hover:bg-blue-100 border-blue-200' :
                        'bg-white hover:bg-gray-50 border-gray-200'
                      } ${
                        // Border styles (Company holidays get special border)
                        isCompanyHoliday ? 'border-2 border-purple-400' :
                        isWeeklyHoliday ? 'border-2 border-gray-500' :
                        isBeforeJoining ? 'border border-gray-400' :
                        isAbsent || (data && data.status === 'Absent') ? 'border-2 border-red-300' :
                        data ? 'border-2' : 'border border-gray-200'
                      } ${
                        // Today's special styling
                        isToday ? 'ring-2 ring-indigo-500 ring-offset-1' : ''
                      }`}
                      onClick={() => {
                        if (day) {
                          const dateStr = `${selectedYear}-${(selectedMonth + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
                          openAttendanceDetails(dateStr, data);
                        }
                      }}
                    >
                      {day && (
                        <>
                          <div className={`text-sm font-medium ${
                            isToday ? 'text-indigo-700 font-bold' : 
                            isCompanyHoliday ? 'text-purple-700 font-bold' :
                            isWeeklyHoliday ? 'text-red-600 font-semibold' :
                            isBeforeJoining ? 'text-gray-500' :
                            isAbsent || (data && data.status === 'Absent') ? 'text-red-700 font-semibold' :
                            data && data.status === 'Present' ? 'text-green-700' :
                            data && data.status === 'Late' ? 'text-yellow-700' :
                            data && data.status === 'Half-day' ? 'text-orange-700' :
                            data && data.status === 'Incomplete' ? 'text-blue-700' :
                            'text-gray-700'
                          }`}>
                            {day}
                            {isToday && <span className="text-xs text-indigo-500 block">Today</span>}
                            {isCompanyHoliday && (
                              <span className="text-xs text-purple-600 block font-bold" title={holidayInfo?.name}>
                                🎉 {holidayInfo?.name?.substring(0, 8) || 'Holiday'}
                              </span>
                            )}
                            {isWeeklyHoliday && !isCompanyHoliday && (
                              <span className="text-xs text-red-500 block font-medium">
                                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][new Date(`${selectedYear}-${(selectedMonth + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`).getDay()]}
                              </span>
                            )}
                            {isBeforeJoining && !isCompanyHoliday && <span className="text-xs text-gray-400 block">Before Joining</span>}
                            {isAbsent && !isCompanyHoliday && <span className="text-xs text-red-500 block font-medium"></span>}
                          </div>
                          {data && (
                            <div className="flex justify-center mt-1">
                              <div className={`w-2 h-2 rounded-full ${getStatusDotColor(data.status)}`}></div>
                            </div>
                          )}
                          {!data && isPastDate && !isBeforeJoining && !isWeeklyHoliday && !isCompanyHoliday && (
                            <div className="flex justify-center mt-1">
                              <div className="w-2 h-2 rounded-full bg-red-500"></div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Enhanced Legend */}
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Calendar Legend</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 text-xs">
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                      <span className="text-green-700 font-medium">Present</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></div>
                      <span className="text-yellow-700 font-medium">Late</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                      <span className="text-red-700 font-medium">Absent/Leave</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-orange-500 mr-2"></div>
                      <span className="text-orange-700 font-medium">Half-day</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-blue-500 mr-2"></div>
                      <span className="text-blue-700 font-medium">Incomplete</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded border-2 border-indigo-500 mr-2"></div>
                      <span className="text-indigo-700 font-medium">Today</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded bg-gray-100 border border-gray-400 mr-2"></div>
                      <span className="text-gray-600 font-medium">Before Joining</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded bg-gray-200 border-2 border-gray-500 mr-2"></div>
                      <span className="text-red-600 font-medium">Weekly Holiday</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded bg-purple-100 border-2 border-purple-400 mr-2"></div>
                      <span className="text-purple-700 font-medium">Company Holiday</span>
                    </div>
                    {loadingHolidays && (
                      <div className="flex items-center col-span-2">
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-purple-600 mr-2"></div>
                        <span className="text-purple-600 font-medium">Loading holidays...</span>
                      </div>
                    )}
                  </div>
                  {/* <div className="mt-3 space-y-2">
                    <div className="p-2 bg-red-50 border border-red-200 rounded text-xs">
                      <span className="text-red-700 font-medium">🔴 Red Background:</span>
                      <span className="text-red-600 ml-1">Days when employee was not present (absent/leave) after joining date, excluding weekly holidays and company holidays</span>
                    </div>
                    <div className="p-2 bg-gray-50 border border-gray-300 rounded text-xs">
                      <span className="text-gray-700 font-medium">⚪ Light Gray:</span>
                      <span className="text-gray-600 ml-1">Days before employee's joining date (attendance not tracked)</span>
                    </div>
                    <div className="p-2 bg-gray-100 border border-gray-400 rounded text-xs">
                      <span className="text-gray-700 font-medium">⬜ Dark Gray:</span>
                      <span className="text-red-600 ml-1">Company weekly holidays (not counted as leaves)</span>
                    </div>
                    <div className="p-2 bg-purple-50 border border-purple-200 rounded text-xs">
                      <span className="text-purple-700 font-medium">🎉 Purple Background:</span>
                      <span className="text-purple-600 ml-1">Company holidays (festivals, special occasions - not counted as leaves)</span>
                    </div>
                    {holidays.length > 0 && (
                      <div className="p-2 bg-blue-50 border border-blue-200 rounded text-xs">
                        <span className="text-blue-700 font-medium">📅 Holiday Summary:</span>
                        <span className="text-blue-600 ml-1">{holidays.length} company holidays loaded for {new Date().getFullYear()}</span>
                      </div>
                    )}
                    {holidaysError && (
                      <div className="p-2 bg-amber-50 border border-amber-200 rounded text-xs">
                        <span className="text-amber-700 font-medium">⚠️ Holiday Error:</span>
                        <span className="text-amber-600 ml-1">{holidaysError}</span>
                      </div>
                    )}
                  </div> */}
                </div>
              </div>
            ) : (
              <div className="overflow-y-auto max-h-96">
                {filteredHistory.length > 0 ? (
                  filteredHistory.map((record, index) => (
                    <div key={index} className="py-3 border-b border-gray-100">
                      <div className="flex justify-between items-center">
                        <div className="font-medium text-gray-700">{record.date}</div>
                        <span className={`px-2 py-1 rounded-full text-sm font-medium ${getStatusColor(record.status)}`}>
                          {record.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 mt-2 text-sm">
                        <div>
                          <div className="text-gray-500">In</div>
                          <div className="font-medium">{record.punchIn}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Out</div>
                          <div className="font-medium">{record.punchOut}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Hours</div>
                          <div className="font-medium">{record.hours}</div>
                        </div>
                      </div>
                      {(record.punchInImage || record.punchOutImage) && (
                        <div className="mt-2 flex space-x-2">
                          {record.punchInImage && (
                            <div className="relative">
                              <img 
                                src={record.punchInImage} 
                                alt="Punch in" 
                                className="w-10 h-10 object-cover rounded border border-gray-300"
                              />
                              <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs text-center">In</div>
                            </div>
                          )}
                          {record.punchOutImage && (
                            <div className="relative">
                              <img 
                                src={record.punchOutImage} 
                                alt="Punch out" 
                                className="w-10 h-10 object-cover rounded border border-gray-300"
                              />
                              <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs text-center">Out</div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No attendance records found for {months[selectedMonth]} {selectedYear}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Notification Component */}
      {notification.show && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg max-w-sm ${
          notification.type === 'success' ? 'bg-green-500 text-white' :
          notification.type === 'error' ? 'bg-red-500 text-white' :
          notification.type === 'warning' ? 'bg-yellow-500 text-white' :
          'bg-blue-500 text-white'
        }`}>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">{notification.message}</span>
            <button
              onClick={() => setNotification({ ...notification, show: false })}
              className="ml-3 text-white hover:text-gray-200"
            >
              <FiX size={18} />
            </button>
          </div>
        </div>
      )}
      
      {/* Attendance Details Modal */}
      {isModalOpen && selectedRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className={`text-xl font-semibold ${
                  selectedRecord.isHoliday ? 'text-purple-800' : 'text-gray-800'
                }`}>
                  {selectedRecord.isHoliday ? '🎉 Holiday Details' : 'Attendance Details'}
                </h3>
                <button 
                  onClick={() => {
                    setIsModalOpen(false);
                    setEditingReport(false);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FiX size={24} />
                </button>
              </div>

              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Date:</span>
                  <span className="font-medium">
                    {new Date(selectedDate).toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </span>
                </div>

                {/* 🎉 HOLIDAY CONTENT SECTION */}
                {selectedRecord.isHoliday && selectedRecord.holidayInfo && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-3">
                    {/* Holiday Header */}
                    <div className="flex items-center justify-center mb-3">
                      <div className="text-4xl mr-3">🎉</div>
                      <div className="text-center">
                        <h4 className="text-lg font-bold text-purple-800">
                          {selectedRecord.holidayInfo.name}
                        </h4>
                        <p className="text-sm text-purple-600">
                          Company Holiday
                        </p>
                      </div>
                    </div>

                    {/* Holiday Details */}
                    <div className="space-y-3">
                      <div className="flex justify-between items-center py-2 border-b border-purple-200">
                        <span className="text-purple-700 font-medium">Holiday Type:</span>
                        <span className="text-purple-800 font-semibold">
                          {selectedRecord.holidayInfo.type || 'General'}
                        </span>
                      </div>
                      
                      {selectedRecord.holidayInfo.description && (
                        <div className="py-2 border-b border-purple-200">
                          <span className="text-purple-700 font-medium block mb-1">Description:</span>
                          <p className="text-purple-800 text-sm leading-relaxed">
                            {selectedRecord.holidayInfo.description}
                          </p>
                        </div>
                      )}
                      
                      <div className="flex justify-between items-center py-2">
                        <span className="text-purple-700 font-medium">Status:</span>
                        <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                          🎉 Holiday
                        </span>
                      </div>
                    </div>

                    {/* Holiday Message */}
                    <div className="bg-purple-100 border border-purple-300 rounded-lg p-3 mt-4">
                      <div className="flex items-center">
                        <div className="text-purple-600 mr-2">ℹ️</div>
                        <div className="text-purple-800 text-sm">
                          <strong>No attendance required:</strong> This is a company holiday. 
                          Employees are not expected to mark attendance on this date.
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Loading State */}
                {selectedRecord.loading && (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    <span className="ml-3 text-gray-600">Loading attendance data...</span>
                  </div>
                )}

                {/* Error State */}
                {selectedRecord.error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <div className="text-red-600 mr-2">⚠️</div>
                      <div className="flex-1">
                        <div className="text-red-800 font-medium">Error Loading Data</div>
                        <div className="text-red-600 text-sm mt-1">{selectedRecord.errorMessage}</div>
                        <button 
                          onClick={() => openAttendanceDetails(selectedDate)}
                          className="mt-2 text-xs text-red-700 hover:text-red-900 underline"
                        >
                          Try Again
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* No Data State */}
                {selectedRecord.noData && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-center">
                      <div className="text-gray-500 mr-2">ℹ️</div>
                      <div className="text-gray-600">No attendance record found for this date</div>
                    </div>
                  </div>
                )}

                {/* Normal State - Only show for non-holiday dates */}
                {!selectedRecord.loading && !selectedRecord.error && !selectedRecord.isHoliday && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status:</span>
                      <span className={`px-2 py-1 rounded-full text-sm ${
                        selectedRecord.noData ? 'bg-gray-100 text-gray-600' : getStatusColor(selectedRecord.status)
                      }`}>
                        {selectedRecord.status}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <div className="text-gray-600">Punch In:</div>
                        <div className="font-medium">{selectedRecord.punchIn || '--:--'}</div>
                        {selectedRecord.punchInImage && !selectedRecord.noData && (
                          <button 
                            onClick={() => viewImage(selectedRecord.punchInImage, 'Punch In Image')}
                            className="text-sm text-blue-600 hover:underline"
                          >
                            View Punch In Image
                          </button>
                        )}
                      </div>
                      <div className="space-y-2">
                        <div className="text-gray-600">Punch Out:</div>
                        <div className="font-medium">{selectedRecord.punchOut || '--:--'}</div>
                        {selectedRecord.punchOutImage && !selectedRecord.noData && (
                          <button 
                            onClick={() => viewImage(selectedRecord.punchOutImage, 'Punch Out Image')}
                            className="text-sm text-blue-600 hover:underline"
                          >
                            View Punch Out Image
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Add Work Hours Row */}
                    <div className="flex justify-between items-center py-2 border-t border-b border-gray-100">
                      <span className="text-gray-600">Work Hours:</span>
                      <span className="font-medium">
                        {selectedRecord.hours || '--:--'}
                      </span>
                    </div>

                    {/* Add Late Time Row */}
                    {selectedRecord.punchIn && selectedRecord.punchIn !== '-' && !selectedRecord.noData && (
                      <div className="flex justify-between items-center py-2 border-t border-b border-gray-100">
                        <span className="text-gray-600">Late Time:</span>
                        <span className="font-medium">
                          {(() => {
                            const { isLate, lateTime } = calculateLateTime(selectedRecord.punchIn);
                            return isLate ? (
                              <span className="text-yellow-600">+{lateTime} late</span>
                            ) : (
                              <span className="text-green-600">On Time</span>
                            );
                          })()}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyAttendance;